import React, { createContext, useContext, useState, useEffect } from 'react';
import { generateId } from '../lib/utils';

const AppContext = createContext();

const INITIAL_PRESETS = [
  { 
    id: 'p1', 
    label: 'Scholarship Fund', 
    unit: 'USD', 
    color: 'bg-blue-400', 
    glow: 'shadow-blue-500/50',
    projects: [
      { id: 'proj1', name: 'STEM Excellence Grant', value: 2500, description: 'Tuition support for high-achieving STEM students.' },
      { id: 'proj2', name: 'Community Arts Fund', value: 1000, description: 'Funding for local youth theater programs.' }
    ]
  },
  { 
    id: 'p2', 
    label: 'Student Mentorship', 
    unit: 'Hours', 
    color: 'bg-purple-400', 
    glow: 'shadow-purple-500/50',
    projects: [
      { id: 'proj3', name: 'Career Coaching', value: 5, description: 'One-on-one session focusing on resume and interview prep.' },
      { id: 'proj4', name: 'Subject Tutoring', value: 2, description: 'Weekly assistance in core academic subjects.' }
    ]
  }
];

const INITIAL_USERS = [
  { 
    id: 'admin', 
    name: 'Galaxy Admin', 
    username: 'admin', 
    password: 'password123', 
    type: 'admin', 
    contributions: [] 
  },
  { 
    id: 'u1', 
    name: 'TechCorp Foundation', 
    username: 'techcorp', 
    password: 'star', 
    type: 'institution', 
    contributions: [
      { id: 'c1', presetId: 'p1', projectId: 'proj1', amount: 2500, status: 'verified', description: 'STEM Excellence Grant', date: '2023-10-01' }
    ] 
  },
  { 
    id: 'u2', 
    name: 'EcoWatch Group', 
    username: 'ecowatch', 
    password: 'earth', 
    type: 'individual', 
    contributions: [
      { id: 'c2', presetId: 'p2', projectId: 'proj3', amount: 5, status: 'verified', description: 'Career Coaching', date: '2023-11-15' }
    ] 
  }
];

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState(INITIAL_USERS);
  const [presets, setPresets] = useState(INITIAL_PRESETS);
  const [joinRequests, setJoinRequests] = useState([]);

  const login = (username, password) => {
    const foundUser = users.find(u => u.username === username && u.password === password);
    if (foundUser) {
      setUser(foundUser);
      return true;
    }
    return false;
  };

  const logout = () => setUser(null);

  const requestToJoin = (details) => {
    const newRequest = { id: generateId(), ...details, status: 'pending', date: new Date().toISOString() };
    setJoinRequests([...joinRequests, newRequest]);
    return true;
  };

  const approveJoinRequest = (requestId, username, password) => {
    const request = joinRequests.find(r => r.id === requestId);
    if (!request) return;
    const newUser = { id: generateId(), name: request.name, type: request.type, username, password, contributions: [] };
    setUsers([...users, newUser]);
    setJoinRequests(joinRequests.filter(r => r.id !== requestId));
  };

  const rejectJoinRequest = (requestId) => {
    setJoinRequests(joinRequests.filter(r => r.id !== requestId));
  };

  const addPreset = (preset) => {
    setPresets([...presets, { ...preset, id: generateId(), projects: [] }]);
  };

  const deletePreset = (id) => {
    setPresets(presets.filter(p => p.id !== id));
  };

  const addProjectToPreset = (presetId, project) => {
    setPresets(presets.map(p => 
      p.id === presetId 
        ? { ...p, projects: [...p.projects, { ...project, id: generateId() }] }
        : p
    ));
  };

  const deleteProjectFromPreset = (presetId, projectId) => {
    setPresets(presets.map(p => 
      p.id === presetId 
        ? { ...p, projects: p.projects.filter(proj => proj.id !== projectId) }
        : p
    ));
  };

  const addContribution = (presetId, projectId, amount, description) => {
    if (!user) return;
    const newContribution = {
      id: generateId(),
      presetId,
      projectId,
      amount: Number(amount),
      description,
      status: 'pending',
      date: new Date().toISOString()
    };
    const updatedUser = { ...user, contributions: [...user.contributions, newContribution] };
    setUser(updatedUser);
    setUsers(users.map(u => u.id === user.id ? updatedUser : u));
  };

  const verifyContribution = (userId, contributionId) => {
    setUsers(users.map(u => {
      if (u.id === userId) {
        return {
          ...u,
          contributions: u.contributions.map(c => 
            c.id === contributionId ? { ...c, status: 'verified' } : c
          )
        };
      }
      return u;
    }));
    
    if (user && user.id === userId) {
      setUser(prev => ({
        ...prev,
        contributions: prev.contributions.map(c => 
          c.id === contributionId ? { ...c, status: 'verified' } : c
        )
      }));
    }
  };

  const galaxyData = users.filter(u => u.type !== 'admin').map(u => ({
    ...u,
    verifiedCount: u.contributions.filter(c => c.status === 'verified').length
  }));

  return (
    <AppContext.Provider value={{
      user, users, presets, joinRequests, galaxyData,
      login, logout, requestToJoin, approveJoinRequest, rejectJoinRequest,
      addPreset, deletePreset, addProjectToPreset, deleteProjectFromPreset, addContribution, verifyContribution
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);